import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, shareReplay, Subject } from 'rxjs';
import { ReportPeriod, TrialBalanceEntry } from '../../models';
import { PaginatedResource } from '@shared/models/paginated-resource';
import { catchError, combineLatestWith, debounceTime, switchMap, take, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import {
  AccountingReportGridComponent,
  AccountingReportColumns,
} from '../../components/accounting-report/accounting-report-grid.component';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { TsSnackbarService } from 'tsui';
import { Store } from '@ngxs/store';
import { AssetManagementState } from '../../state/asset-management.state';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { AssetManagementMessages } from '../../utils/asset-management-messages.utils';
import { AssetManagementLabels } from '../../enums/asset-management-reports.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetManagementFileSources } from '../../models';
import { GraphqlFilterSort } from '@shared/models/graphql';
import { AssetManagementRoutingService } from '../../services/asset-management-routing.service';
import { AssetManagementService } from '../../services/asset-management.service';

@Component({
  selector: 'trial-balance-container',
  standalone: true,
  imports: [AsyncPipe, AccountingReportGridComponent],
  template: ` <accounting-report-grid
    [config]="{
      title: AssetManagementReports.TrialBalance,
      enableImport: !isGlobalGeneralLedger,
      enableExport: false,
      columns: trialBalanceColumns,
    }"
    [reportPeriod]="sharedReportPeriodFilter$ | async"
    [accountingReportPage]="trialBalancePage$ | async"
    [accountingReportLoading]="accountingReportLoading | async"
    (importAccountingReport)="onImportTrialBalance($event.file, $event.source)"
    (sortChanged)="sortTrigger.next($event)"
    (reportPeriodChanged)="reportPeriodTrigger.next($event)"
    (pageChanged)="pageTrigger.next($event)"
  ></accounting-report-grid>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrialBalanceContainerComponent {
  protected readonly AssetManagementReports = AssetManagementLabels;
  readonly store = inject(Store);
  readonly destroyRef = inject(DestroyRef);
  readonly assetManagementService = inject(AssetManagementService);
  readonly trialBalancePage$: Observable<PaginatedResource<TrialBalanceEntry>> = this.store.select(
    AssetManagementState.trialBalance,
  );
  readonly assetManagementRoutingService = inject(AssetManagementRoutingService);

  readonly snackbar = inject(TsSnackbarService);
  readonly trialBalanceColumns: AccountingReportColumns<
    TrialBalanceEntry & { 'gl_account.gl_account_number': string; 'gl_account.gl_account_name': string }
  > = {
    'gl_account.gl_account_number': {
      label: 'Account Number',
      type: 'text',
      minWidth: 300,
    },
    'gl_account.gl_account_name': {
      label: 'Account Name',
      type: 'text',
      minWidth: 300,
    },
    starting_balance: {
      label: 'Starting Balance',
      type: 'money',
    },
    ending_balance: {
      label: 'Ending Balance',
      type: 'money',
    },
  };

  accountingReportLoading = new BehaviorSubject<boolean>(true);
  pageTrigger = new BehaviorSubject<{
    page: number;
    per_page: number;
  }>(this.store.selectSnapshot(AssetManagementState.trialBalancePage));
  readonly sharedReportPeriodFilter$ = this.store.select(AssetManagementState.sharedReportPeriodFilter);
  readonly reportPeriodTrigger = new BehaviorSubject<ReportPeriod>(
    this.store.selectSnapshot(AssetManagementState.sharedReportPeriodFilter),
  );
  readonly sortTrigger = new BehaviorSubject<GraphqlFilterSort[]>([]);
  readonly queryParamsLoaded = new Subject<void>();
  readonly importLoading = new BehaviorSubject<boolean>(false);

  get propertyId(): number {
    return this.store.selectSnapshot(AssetManagementState.propertyId);
  }

  get isGlobalGeneralLedger(): boolean {
    return !this.propertyId;
  }

  ngOnInit() {
    this.pageTrigger
      .pipe(
        combineLatestWith(this.reportPeriodTrigger, this.sortTrigger, this.queryParamsLoaded),
        debounceTime(500),
        tap(() => this.accountingReportLoading.next(true)),
        switchMap(([pagination, reportPeriod, sort]) => {
          return this.store.dispatch(
            new AssetManagementActions.LoadTrialBalance({
              page: pagination.page,
              per_page: pagination.per_page,
              report_period: reportPeriod,
              property_id: this.propertyId,
              sort,
            }),
          );
        }),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          null,
          AssetManagementMessages.Error.LOAD(AssetManagementLabels.TrialBalance),
        ),
        catchError((err) => {
          this.accountingReportLoading.next(false);

          return EMPTY;
        }),
        shareReplay(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.accountingReportLoading.next(false);
        this.assetManagementRoutingService.updateRouteQueryParams({
          key: this.reportPeriodTrigger.value.key,
          start_date: this.reportPeriodTrigger.value.start_date,
          end_date: this.reportPeriodTrigger.value.end_date,
        });
      });

    this.assetManagementRoutingService.retrieveQueryParamsFilters(this.reportPeriodTrigger, this.queryParamsLoaded);
  }

  onImportTrialBalance($event: File, source: AssetManagementFileSources) {
    this.importLoading.next(true);

    this.assetManagementService
      .importTrialBalance($event, this.propertyId, source)
      .pipe(take(1))
      .subscribe(() => {
        this.reportPeriodTrigger.next(this.reportPeriodTrigger.value);
      });
  }
}
